import {createApp, defineAsyncComponent} from 'vue';

import App from '@/App.vue';

import '@assets/svg_icons/fygr-icons.scss';
import '@/assets/style.scss';
import '@/assets/fonts.scss';
import 'v-calendar/style.css';

const VueObserveVisibility = () => import('vue3-observe-visibility');
import FontAwesomeIcon from '@/plugins/FontAwesome';
import SegmentAnalytics from '@/plugins/SegmentAnalytics';
import BasePopup from '@/components/elements/popups/BasePopup.vue';
import {createGtm} from '@gtm-support/vue-gtm';
import router from '@/router/router';
import store from '@/store';
import {vueErrorHandler} from '@/utils/errorHandler.js';
import {registerSW} from 'virtual:pwa-register';

const app = createApp({
  extends: App,
})
  .use(router)
  .use(store)
  .use(VueObserveVisibility)
  .use(SegmentAnalytics)
  .use(
    createGtm({
      id: 'GTM-565JZRV',
      loadScript: false,
      vueRouter: router,
    }),
  )
  .component('FontAwesomeIcon', FontAwesomeIcon)
  .component('BasePopup', BasePopup)
  .component(
    'FygrSelectableTable',
    defineAsyncComponent(
      () => import('@/components/elements/FygrSelectableTable.vue'),
    ),
  )
  .component(
    'FygrButton',
    defineAsyncComponent(() => import('@/components/elements/FygrButton.vue')),
  )
  .component(
    'IconButton',
    defineAsyncComponent(() => import('@/components/elements/IconButton.vue')),
  )
  .component(
    'FygrInput',
    defineAsyncComponent(() => import('@/components/elements/FygrInput.vue')),
  )
  .component(
    'LuPlaceholder',
    defineAsyncComponent(
      () => import('@/components/elements/LuPlaceholder.vue'),
    ),
  )
  .component(
    'Checkbox',
    defineAsyncComponent(() => import('@/components/elements/Checkbox.vue')),
  )
  .component(
    'Toggle',
    defineAsyncComponent(() => import('@/components/elements/Toggle.vue')),
  )
  .component(
    'RangedInput',
    defineAsyncComponent(() => import('@/components/elements/RangedInput.vue')),
  )
  .component(
    'Spinner',
    defineAsyncComponent(() => import('@/components/elements/Spinner.vue')),
  )
  .component(
    'Popup',
    defineAsyncComponent(() => import('@/components/elements/Popup.vue')),
  )
  .component(
    'Loader',
    defineAsyncComponent(() => import('@/components/elements/Loader.vue')),
  )
  .component(
    'Container',
    defineAsyncComponent(() => import('@/components/elements/Container.vue')),
  )
  .component(
    'NoNavigation',
    defineAsyncComponent(
      () => import('@/components/layouts/NoNavigationLayout.vue'),
    ),
  )
  .component(
    'NoAuthLayout',
    defineAsyncComponent(() => import('@/components/layouts/NoAuthLayout.vue')),
  )
  .component(
    'EmptyLayout',
    defineAsyncComponent(() => import('@/components/layouts/EmptyLayout.vue')),
  )
  .component(
    'SkeletonPreloader',
    defineAsyncComponent(
      () => import('@/components/elements/SkeletonPreloader.vue'),
    ),
  )
  .mixin({
    mounted() {
      this.$el.__vue__ = this;
    },
  });
if (import.meta.env.VITE_APP_ENV === 'live') {
  app.config.errorHandler = vueErrorHandler;
}

const updateSW = registerSW({
  onNeedRefresh() {
    updateSW(true);
  },
});

app.mount('#app');

store.commit('user/initStorage', null, {root: true});

// mandatory: weglot can be initialize but not ready to switch
// If we go in 'actionGetUserPreferences' before switchersReady, this listener will allow to synchro weglot with store
// If we listen 'switchersReady' before 'actionGetUserPreferences', the 'actionGetUserPreferences' will work correctly
window.Weglot.on('switchersReady', function () {
  window.Weglot.switchTo(
    store.getters['user/getLocale'] || window.Weglot.getBestAvailableLanguage(),
  );
});

const wg = JSON.parse(localStorage.getItem('wg-translations'));
if (wg && wg['gt4543-clean-weglot-mess'] !== true) {
  for (const key in wg) {
    if (wg[key] && wg[key].createdTime <= 1714010400000) {
      localStorage.removeItem('wg-translations');
      localStorage.setItem(
        'wg-translations',
        JSON.stringify({'gt4543-clean-weglot-mess': true}),
      );
      break;
    }
  }
}

window.Weglot.initialize({
  api_key:
    import.meta.env.VITE_APP_ENV === 'live'
      ? 'wg_8ab3aff22b808085d046f8eebec9f4a42'
      : 'wg_f1ae7d22c57dbf2ab34503156d75c9668',
  switchers: null,
  cache: true,
  whitelist: [
    {
      value: '.translate',
    },
  ],
});
